import React, { useEffect, useRef, useState } from 'react'
import { fetchAndSetPostMethodData } from '../commonSlice'
import {
  getEndpointFromConfig,
  getTimePeriod,
  getYearsTimePeriod,
} from '../utils/helper'
import { endpointLabels } from '../utils/enum'
import { useDispatch, useSelector } from 'react-redux'
import ChartCardContentLoader from './CardContentLoader/ChartCardContentLoader'
import SectionCardContentLoader from './CardContentLoader/SectionCardContentLoader'

const FeaturedDatasets = ({
  topicId = '',
  themeId,
  filterFeaturedDataset,
  pageName,
  countryId,
}) => {
  const { endpointList, apiKeyName, apiKeyValue, featureProps, hierarchyData } =
    useSelector((state) => state.common)
  const [datasets, setDatasets] = useState([])
  const [datasetCount, setDatasetCount] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [themeIdNo, setThemeIdNo] = useState([])
  const featDatasetCompRef = useRef(null)
  const {
    authorDatasetLink,
    publishDatasetLink,
    mode,
    searchURLPublish,
    searchURLAuthor,
    selector,
    noDataImageURL,
  } = featureProps
  const dispatch = useDispatch()

  const getDatasetUrl = (datasetId) => {
    return mode === 'author'
      ? `${authorDatasetLink}.${datasetId}${
          countryId ? `?country=${countryId}` : ''
        }.html`
      : `${publishDatasetLink}/${datasetId}${
          countryId ? `?country=${countryId}` : ''
        }`
  }

  let searchUrl = ''
  if (mode === 'publish') {
    searchUrl = searchURLPublish
  } else {
    searchUrl = searchURLAuthor
  }

  useEffect(() => {
    if (hierarchyData?.codes) {
      hierarchyData?.codes.map((obj) => {
        if (obj.value === themeId) {
          setThemeIdNo(obj.id)
        }
      })
    }
  }, [themeId, hierarchyData])

  const fetchFeaturedDatasets = async () => {
    try {
      const request = {
        count: true,
        facets: [],
        orderby: 'series_description/date_last_update desc',
        select:
          'series_description/economies_count, series_description/database_id, metadata_information/title, series_description/time_periods',
        top: 4,
        filter: filterFeaturedDataset,
      }
      const headers = apiKeyName ? { [apiKeyName]: apiKeyValue } : undefined
      const metadataResponse = await dispatch(
        fetchAndSetPostMethodData({
          endpointData: {
            endpoint: getEndpointFromConfig(
              endpointList,
              endpointLabels.Metadata
            ).apiEndpoint,
            method: 'POST',
            data: request,
            headers,
          },
          key:
            pageName !== 'countryProfile'
              ? pageName !== 'countryThemeProfile'
                ? `featureddataset_${themeId}_${topicId}`
                : `featureddataset_${countryId}_${themeId}`
              : `featureddataset_${countryId}`,
        })
      )

      if (metadataResponse?.payload?.value?.length > 0) {
        let sortedDatasets = [...metadataResponse.payload.value]
        // update the latest datasets in alphabetic order of their titles
        sortedDatasets.sort((a, b) =>
          a?.metadata_information?.title.localeCompare(
            b?.metadata_information?.title
          )
        )
        setDatasets(sortedDatasets)
      } else setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error('Error fetching metadata in Featured Datasets:', error)
    }
  }

  const fetchIndicatorCount = async (databaseId) => {
    try {
      const request = {
        count: true,
        facets: [],
        orderby: '',
        select: '',
        top: 0,
        filter: `type eq 'indicator' and series_description/database_id eq '${databaseId}'`,
      }
      const headers = apiKeyName ? { [apiKeyName]: apiKeyValue } : undefined
      const metadataResponse = await dispatch(
        fetchAndSetPostMethodData({
          endpointData: {
            endpoint: getEndpointFromConfig(
              endpointList,
              endpointLabels.Metadata
            ).apiEndpoint,
            method: 'POST',
            data: request,
            headers,
          },
          key: `featureddatasetindicatorcount_${databaseId}`,
        })
      )

      if (metadataResponse?.payload) {
        const existingEntry = datasetCount.find((obj) => obj.id === databaseId)

        if (!existingEntry) {
          setDatasetCount((prevCount) => [
            ...prevCount,
            {
              id: databaseId,
              count: metadataResponse.payload['@odata.count'],
            },
          ])
        }
        setIsLoading(false)
      } else setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error(
        'Error fetching metadata in Featured Datasets Indicator count:',
        error
      )
    }
  }

  useEffect(() => {
    if (datasets.length) {
      datasets.forEach((obj) => {
        fetchIndicatorCount(obj.series_description.database_id)
      })
    }
  }, [datasets])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (
              (pageName === 'themeProfile' && themeId) ||
              (pageName === 'countryProfile' && countryId) ||
              (pageName === 'topicProfile' && themeId && topicId) ||
              (pageName === 'countryThemeProfile' && themeId && countryId) ||
              (pageName === 'countryAnalyticsProfile' && themeId && countryId)
            ) {
              setIsLoading(true)
              fetchFeaturedDatasets()
            }
            observer.unobserve(entry.target)
          }
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    )

    if (featDatasetCompRef.current) {
      observer.observe(featDatasetCompRef.current)
    }

    return () => observer.disconnect()
  }, [topicId, themeId, pageName, countryId])

  const getDatasetCountJSX = (datasetId) => {
    const temp = datasetCount.find((obj) => obj.id === datasetId)
    return temp ? temp.count : 0
  }

  return !isLoading && (!datasetCount || datasetCount.length === 0) ? null : (
    <div
      className="container responsivegrid padd-b-48 touchui-container aem-GridColumn aem-GridColumn--default--122"
      id="datasetsAndSurveys"
    >
      <div className="cmp-container">
        <div className="container responsivegrid aem-GridColumn aem-GridColumn--default--12">
          <div className="aem-Grid">
            <div className="title aem-GridColumn--xl--9 aem-GridColumn--default--9 aem-GridColumn--desktop--9 aem-GridColumn--tablet--9 aem-GridColumn--phone--12 aem-GridColumn--xs--12">
              <div className="cmp-title">
                <h3 ref={featDatasetCompRef} className="cmp-title__text">
                  Featured Datasets
                </h3>
              </div>
            </div>
            <div className="button view-all-link aem-GridColumn--xl--3 aem-GridColumn--default--3 aem-GridColumn--desktop--3 aem-GridColumn--tablet--3 aem-GridColumn--phone--12 aem-GridColumn--xs--12">
              <a
                className="cmp-button"
                href={`${searchUrl}?${
                  pageName !== 'countryProfile'
                    ? pageName !== 'countryThemeProfile'
                      ? `themeAndTopics=${topicId || themeIdNo}&tab=dataset`
                      : `themeAndTopics=${themeId}&country=${countryId}&tab=dataset`
                    : `country=${countryId}&tab=dataset`
                }`}
                data-customlink="nl:body content"
              >
                <span className="cmp-button__text">VIEW ALL</span>
              </a>
            </div>
          </div>
        </div>
        <div className="container responsivegrid aem-GridColumn aem-GridColumn--default--12">
          <div className="swiper tui_card_swiper swiper-container">
            {!isLoading && datasetCount.length ? (
              <div className="swiper-wrapper tui_column_grid">
                {datasets.map((obj) => (
                  <div
                    key={obj.series_description.database_id}
                    className="swiper-slide tui_data_card tui_data_card_sm"
                  >
                    <div className="tui_card_wrapper">
                      <div className="tui_card_content_right">
                        <h3 className="tui_card_title_text">
                          <a
                            href={getDatasetUrl(
                              obj.series_description.database_id
                            )}
                            data-customlink="nl:body content"
                          >
                            {obj.metadata_information.title}
                          </a>
                        </h3>
                        <div className="tui_card_content_left">
                          <ul className="tui_card_data">
                            <li className="tui_card_data_item">
                              <p className="tui_card_data_title">Indicators</p>
                              <p className="tui_card_data_value">
                                <span>
                                  {getDatasetCountJSX(
                                    obj.series_description.database_id
                                  )}
                                </span>
                              </p>
                            </li>
                            <li className="tui_card_data_item">
                              <p className="tui_card_data_title">{`${
                                obj.series_description.economies_count > 1
                                  ? 'Economies'
                                  : 'Economy'
                              }`}</p>
                              <p className="tui_card_data_value">
                                <span>
                                  {obj.series_description.economies_count}
                                </span>
                              </p>
                            </li>
                            {obj.series_description.time_periods?.[0] && (
                              <li className="tui_card_data_item">
                                <p className="tui_card_data_title">
                                  {obj.series_description.time_periods[0]
                                    .start &&
                                  obj.series_description.time_periods[0].end
                                    ? 'Years'
                                    : 'Year'}
                                </p>
                                <p className="tui_card_data_value">
                                  <span>{getYearsTimePeriod(obj) || '--'}</span>
                                </p>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <SectionCardContentLoader />
            )}
            <div
              className="swiper-button-next"
              aria-hidden="true"
              tabIndex="-1"
              style={{ display: 'none' }}
            ></div>
            <div
              className="swiper-button-prev"
              aria-hidden="true"
              tabIndex="-1"
              style={{ display: 'none' }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturedDatasets
